import React, { useState, useEffect } from 'react';
import { useWebsiteData } from '../../../context/WebsiteDataContext';
import { uploadFile } from '../../../services/contentApi';

const BlogManager = () => {
  const { blogs, addBlog, updateBlog, deleteBlog, toggleBlogStatus } = useWebsiteData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [featuredImage, setFeaturedImage] = useState('');

  // Reset image when modal opens/closes or editing changes
  useEffect(() => {
    if (editingBlog) {
      setFeaturedImage(editingBlog.featuredImage || '');
    } else {
      setFeaturedImage('');
    }
  }, [editingBlog, showAddModal]);

  const filteredBlogs = filterStatus === 'all'
    ? blogs
    : blogs.filter(b => b.status === filterStatus);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const result = await uploadFile(file, 'blogs');
      setFeaturedImage(result.url);
    } catch (error) {
      console.error("Blog image upload failed:", error);
      alert("Failed to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const handleAddBlog = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const blogData = {
      title: formData.get('title'),
      excerpt: formData.get('excerpt'),
      content: formData.get('content'),
      author: formData.get('author'),
      category: formData.get('category'),
      status: formData.get('status'),
      tags: formData.get('tags').split(',').map(tag => tag.trim()).filter(tag => tag),
      featuredImage: featuredImage || '/assets/images/no_image.png',
      publishDate: new Date().toISOString()
    };

    try {
      await addBlog(blogData);
      setShowAddModal(false);
    } catch (error) {
      alert("Failed to create blog post.");
    }
  };

  const handleEditBlog = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const blogData = {
      title: formData.get('title'),
      excerpt: formData.get('excerpt'),
      content: formData.get('content'),
      author: formData.get('author'),
      category: formData.get('category'),
      status: formData.get('status'),
      tags: formData.get('tags').split(',').map(tag => tag.trim()).filter(tag => tag),
      featuredImage: featuredImage,
      publishDate: editingBlog.publishDate || new Date().toISOString()
    };

    try {
      await updateBlog(editingBlog.id, blogData);
      setEditingBlog(null);
    } catch (error) {
      alert("Failed to update blog post.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Blog Management</h2>
          <p className="text-gray-600">Manage blog posts and articles</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-custom-brown text-white px-4 py-2 rounded-lg hover:bg-custom-brown/90 transition-colors duration-200"
        >
          Create New Post
        </button>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['all', 'published', 'draft'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${filterStatus === status
                ? 'border-custom-brown text-custom-brown'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {status} ({blogs.filter(b => status === 'all' ? true : b.status === status).length})
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-4">
        {filteredBlogs.map((blog) => (
          <div key={blog.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {blog.featuredImage && (
                    <img src={blog.featuredImage} alt="" className="w-12 h-12 rounded object-cover mr-4" />
                  )}
                  <h3 className="text-xl font-semibold text-gray-900">{blog.title}</h3>
                </div>
                <p className="text-gray-600 mb-3">{blog.excerpt}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                  <span>By {blog.author}</span>
                  <span>•</span>
                  <span>{blog.category}</span>
                  <span>•</span>
                  <span>{blog.publishDate}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-custom-brown/10 text-custom-brown text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${blog.status === 'published'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
                }`}>
                {blog.status}
              </span>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setEditingBlog(blog)}
                className="bg-custom-brown/10 text-custom-brown px-3 py-2 rounded text-sm hover:bg-custom-brown/20 hover:text-custom-brown transition-colors duration-200"
              >
                Edit
              </button>
              <button
                onClick={() => toggleBlogStatus(blog.id)}
                className="bg-custom-brown/10 text-custom-brown px-3 py-2 rounded text-sm hover:bg-custom-brown/20 transition-colors duration-200"
              >
                {blog.status === 'published' ? 'Unpublish' : 'Publish'}
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this blog post?')) {
                    deleteBlog(blog.id);
                  }
                }}
                className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm hover:bg-red-100 hover:text-red-700 transition-colors duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Blog Modal */}
      {(showAddModal || editingBlog) && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddModal(false);
              setEditingBlog(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h3>
            </div>

            <form onSubmit={editingBlog ? handleEditBlog : handleAddBlog} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    defaultValue={editingBlog?.title || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Featured Image
                  </label>
                  <div className="flex items-center space-x-4">
                    {featuredImage && (
                      <img src={featuredImage} alt="Featured" className="w-20 h-20 object-cover rounded border border-gray-200" />
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-custom-brown/10 file:text-custom-brown hover:file:bg-custom-brown/20 transition-colors cursor-pointer"
                      />
                    </div>
                  </div>
                  {uploading && <p className="text-sm text-custom-brown mt-1">Uploading...</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Excerpt
                  </label>
                  <textarea
                    name="excerpt"
                    rows="3"
                    defaultValue={editingBlog?.excerpt || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Content
                  </label>
                  <textarea
                    name="content"
                    rows="10"
                    defaultValue={editingBlog?.content || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Write your blog content here..."
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Author
                    </label>
                    <input
                      type="text"
                      name="author"
                      defaultValue={editingBlog?.author || 'Nirmitee Team'}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Category
                    </label>
                    <input
                      type="text"
                      name="category"
                      defaultValue={editingBlog?.category || ''}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      defaultValue={editingBlog?.status || 'published'}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    defaultValue={editingBlog?.tags?.join(', ') || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    placeholder="construction, pune, trends"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingBlog(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 bg-custom-brown text-white px-4 py-2 rounded-md hover:bg-custom-brown/90 transition-colors disabled:opacity-50 flex items-center justify-center font-medium shadow-sm"
                >
                  {editingBlog ? 'Update' : 'Create'} Post
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager; 